import { useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  GetHealthProfessionalItemsFromOtherHealthProviderDocument,
  GetHealthProfessionalItemsFromOtherHealthProviderQueryVariables,
  HealthProfessionalItemDataFragment,
  HealthProfessionalStatus,
} from '../../types/api/graphql';
import { AllPracticesId } from '../types';

const getStatuses = (statuses: (HealthProfessionalStatus | 'ALL')[]) => {
  if (statuses.includes('ALL')) {
    return [
      HealthProfessionalStatus.Active,
      HealthProfessionalStatus.Inactive,
      HealthProfessionalStatus.Pending,
    ];
  }

  return statuses as HealthProfessionalStatus[];
};

export function useHealthProfessionalItemsFromOtherHealthProvider(
  variables: Omit<
    GetHealthProfessionalItemsFromOtherHealthProviderQueryVariables,
    'statuses'
  > & {
    statuses?: (HealthProfessionalStatus | 'ALL')[];
  }
) {
  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useCacheQuery(GetHealthProfessionalItemsFromOtherHealthProviderDocument, {
    variables: {
      ...variables,
      healthProviderId:
        variables.healthProviderId !== AllPracticesId
          ? variables.healthProviderId
          : null,
      statuses: getStatuses(variables.statuses ?? ['ALL']),
    },
    skip: !variables.healthProviderId,
  });

  // Subscriptions

  const data = useMemo(() => {
    if (healthProfessionalItemsData?.healthProfessionalsOtherHealthProviders) {
      return {
        ...healthProfessionalItemsData.healthProfessionalsOtherHealthProviders,
        items: (
          healthProfessionalItemsData.healthProfessionalsOtherHealthProviders
            .items ?? []
        ).map(option => option as HealthProfessionalItemDataFragment),
      };
    }

    return null;
  }, [healthProfessionalItemsData?.healthProfessionalsOtherHealthProviders]);

  return {
    data,
    loading: healthProfessionalItemsLoading,
  };
}
